import React from 'react';
import { Route, Routes } from 'react-router';
import queryString from 'query-string';
import PropTypes from 'prop-types';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { AnimatePresence } from 'framer-motion';
import DashboardPage from '../components/DashboardPage/DashboardPage';
import WidgetPage from '../components/WidgetPage/WidgetPage';
import Authenticating from '../components/Authenticating';
import {
  addCriticalBreadcrumbToSentry,
  addInfoBreadcrumbToSentry,
  sendToSentry,
  setHostInSentryScope,
  setLocaleInSentryScope,
  setUserInSentryScope
} from '../libs/sentryErrorLogging';
import { withRouter } from '../libs/widgetUtilities';
import HomePageHeader from '../components/PageHeaders/HomePageHeader';
import YesplanLocationUrlWrapper from '../components/YesplanLocationUrlWrapper';
import ErrorPage from '../components/ErrorPage/ErrorPage';
import DelayRenderComponent from '../components/DelayRenderComponent';
import RequestHandler from '../libs/requestHandling';
import NetworkDisconnectionBoundary from '../components/NetworkDisconnectionBoundary';
import SessionStorageContext from '../components/SessionStorageContext';
import {
  sessionStorageItemNameAppJWT,
  sessionStorageItemNameEmptyCustomData,
  sessionStorageItemNameEventsListPagination
} from '../libs/sessionStorageItemNames';
import {
  addToHomeScreenSetup,
  installPushNotifications,
  sendPushNotification
} from '../libs/pwa';
import {
  startPollingForNotifications,
  stopPollingForNotifications
} from '../libs/notificationsPolling';
import WidgetPageHeader from '../components/PageHeaders/WidgetPageHeader';
import NavigationHistory from '../libs/NavigationHistory';
import ErrorBoundary from '../components/ErrorBoundary/ErrorBoundary';
import FeatureFlagProvider from '../components/FeatureFlagProvider';
import setupAnalytics from '../libs/analytics';
import CacheResultsContext from '../components/CacheResultsContext';
import DetectServerUpdateBoundary from '../components/DetectServerUpdateBoundary';
import { reformatRejectedCallsOutOfFilterOptions } from '../libs/ypAPIDataFormatter';
import { defaultTimeFormat } from '../libs/timeFormatting';
import FormatSettingsContext from '../components/FormatSettingsContext';
import RosterNotifications from '../yesplan_classes/YPRosterNotificationsObject';
import UpdateNotifications from '../yesplan_classes/YPUpdateNotificationsObject';
import TimesheetNotifications from '../yesplan_classes/YPTimesheetNotificationsObject';
import {
  deserializeYesplanAPIDateAndTime,
  todayDateObject
} from '../libs/dateTimeUtilities';
import { popoverWidgets } from '../widgets';
import { ErrorWithHTTPStatusCode, isInternalError } from '../libs/errors';
import {
  capture,
  identify,
  PH_SETTING_DEFAULT_EVENT_TAB_CHANGED,
  PH_USER_LOGGED_IN,
  identifyProps,
  getTenantFromHost
} from '../libs/posthogAnalytics';


require('moment/locale/nl');
require('moment/locale/fr');

class YesplanAppFrame_ extends React.Component {
  constructor(props) {
    super(props);

    this.processAuth = this.processAuth.bind(this);
    this.state = {
      isAccessForbidden: false,
      isAuthenticating: true,
      authenticationTime: false,
      user: null,
      isAuthenticated: false,
      dayline: '00:00',
      rosternotifications: new RosterNotifications([]),
      updatenotifications: new UpdateNotifications([]),
      timesheetnotifications: new TimesheetNotifications([]),
      showNotificationCenter: false,
      showSettingsCenter: false,
      customRendererForHeaderTitle: false,
      /* This counter's sole purpose is to update child components after a session storage item has changed its value */
      sessionStorageUpdateCounter: 0,
      /* This counter's sole purpose is to update child components after caching results have changed */
      cachingResultsUpdateCounter: 0,
      /* To avoid having to fetch the options every time the Events widget is fetched, store them here */
      eventsFilterOptions: null,
      dateFormatSetting: null,
      timeFormatSetting: null,
      statuses: {},
      profiles: {}
    };

    const { history, linkUrlFor } = props;

    this.previousURL = false;
    this.NavigationHistory = new NavigationHistory(
      history,
      linkUrlFor,
      props.router.navigate
    );
    this.widgetPageHeaderRef = React.createRef();
    this.widgetScrollContainerRef = React.createRef();

    this.sessionStorageDictionary = {
      [sessionStorageItemNameEmptyCustomData]: false,
      [sessionStorageItemNameEventsListPagination]: {}
    };
    Object.keys(this.sessionStorageDictionary).forEach((key) => {
      if (sessionStorage.getItem(key) === null)
        sessionStorage.setItem(key, JSON.stringify(this.sessionStorageDictionary[key]));
    });
    this.sessionStorageContext = {
      getSessionStorageValue: (key) => JSON.parse(sessionStorage.getItem(key)),
      setSessionStorageValue: (key, value, reRenderAfterUpdate = true) => {
        sessionStorage.setItem(key, JSON.stringify(value));
        if (reRenderAfterUpdate)
          this.setState({
            sessionStorageUpdateCounter: this.state.sessionStorageUpdateCounter + 1
          });
      }
    };
    this.cacheResultsContext = {
      getCacheResultValue: (key) => {
        if (this.state.cache) return this.state.cache[key];
        return null;
      },
      setCacheResultValue: (key, value, reRenderAfterUpdate = true) => {
        let updateStateObj = reRenderAfterUpdate
          ? { cachingResultsUpdateCounter: this.state.cachingResultsUpdateCounter + 1 }
          : {};
        if (this.state.cache) {
          updateStateObj = {
            cache: { ...this.state.cache, [key]: value },
            ...updateStateObj
          };
          this.setState(updateStateObj);
        } else {
          updateStateObj = { cache: { [key]: value }, ...updateStateObj };
          this.setState(updateStateObj);
        }
      },
      resetCacheResultValue: (key) => {
        if (this.state.cache)
          this.setState({
            cache: { ...this.state.cache, [key]: null },
            cachingResultsUpdateCounter: this.state.cachingResultsUpdateCounter + 1
          });
      }
    };

    this.functionsForChildComponents = [
      'toggleNotificationUnreadStatus',
      'markAllNotificationsAsRead',
      'setUserPreferenceRosterNotificationEMails',
      'setUserPreferenceDefaultEventInspectorTabApp',
      'setUserPreferenceEventFilters',
      'logoutOfApp',
      'setCustomRendererForHeaderTitle',
      'fetchEventFilterOptions',
      'showNotificationsInHeaderForWidget',
      'renderHeaderForWidgetPage'
    ];

    this.functionsForChildComponents.forEach((eventHandler) => {
      this[eventHandler] = this[eventHandler].bind(this);
    });
  }

  componentDidMount() {
    const { jwt } = queryString.parse(this.props.location.search);
    if (!this.state.isAuthenticated && jwt) this.requestAuthWithToken(jwt);
    else this.requestAuthWithSessionStorageJWT();

    // Make :active pseudoselector on actions work for mobile browsers
    document.addEventListener('touchstart', () => {}, false);
    /* Insert path for manifest.json with the host and domain segments added */
    document
      .querySelector('#manifest-element')
      .setAttribute('href', this.props.linkUrlFor('/manifest.json'));
    /* Setup analytics */
    setupAnalytics();
  }

  componentWillUnmount() {
    stopPollingForNotifications();
  }

  setupRoutingPaths(viewableWidgets) {
    const { routeUrlFor } = this.props;
    this.routes = [{ exact: true, path: routeUrlFor('/') }];
    popoverWidgets.forEach((widget) =>
      this.routes.push({ exact: true, path: routeUrlFor(`/${widget.id}`) })
    );
    viewableWidgets.forEach((widget) =>
      this.routes.push({ path: routeUrlFor(`/${widget.id}/*`) })
    );
  }

  getNotifications(ypversion) {
    const { user, authenticationTime, humanResource } = this.state;
    startPollingForNotifications(
      user.host,
      user.id,
      ypversion,
      (newRosternotifications, newUpdatenotifications, newTimesheetnotifications) => {
        /* Fetch from state inside this callback function to retrieve the actual latest values.
         * Otherwise, this would cause the PWA alert to be sent every x seconds even if no new notifications came in */
        const { rosternotifications, updatenotifications, timesheetnotifications } = this.state;
        const oldVSUnreadRosterNotifications = rosternotifications.versionStampUnread;
        const oldVSUnreadUpdateNotifications = updatenotifications.versionStampUnread;
        const oldVSUnreadTimesheetNotifications = timesheetnotifications.versionStampUnread;
        const oldVSRosterNotifications = rosternotifications.versionStamp;
        const oldVSUpdateNotifications = updatenotifications.versionStamp;
        const oldVSTimesheetNotifications = timesheetnotifications.versionStamp;
        const newPublishedUntilDate = rosternotifications.updateNotifications(
          newRosternotifications,
          authenticationTime
        );
        timesheetnotifications.updateNotifications(newTimesheetnotifications);
        updatenotifications.updateNotifications(newUpdatenotifications);
        if (
          !rosternotifications.hasEqualUnreadVersionStamp(
            oldVSUnreadRosterNotifications
          ) ||
          !updatenotifications.hasEqualUnreadVersionStamp(oldVSUnreadUpdateNotifications) ||
          !timesheetnotifications.hasEqualUnreadVersionStamp(
            oldVSUnreadTimesheetNotifications
          )
        )
          sendPushNotification(i18n('New notifications for you!'));
        const newState = {};

        if (newPublishedUntilDate && humanResource !== null) {
          newState.humanResource = {
            ...humanResource,
            rosterpublisheduntil: newPublishedUntilDate
          };
        }
        if (
          newPublishedUntilDate ||
          !rosternotifications.hasEqualVersionStamp(oldVSRosterNotifications) ||
          !updatenotifications.hasEqualVersionStamp(oldVSUpdateNotifications) ||
          !timesheetnotifications.hasEqualVersionStamp(oldVSTimesheetNotifications)
        )
          this.setState(newState);
      }
    );
  }

  setUserPreferenceRosterNotificationEMails(newValue) {
    return new RequestHandler().putWithRetry(
      '/preferences',
      {
        yesplanurl: this.state.user.host,
        userid: this.state.user.id
      },
      {
        rosternotifications: { email: newValue }
      },
      () => {
        const newUser = { ...this.state.user };
        newUser.preferences.rosternotifications.email = newValue;
        this.setState({ user: newUser });
      }
    );
  }

  setUserPreferenceDefaultEventInspectorTabApp(newValue) {
    capture(PH_SETTING_DEFAULT_EVENT_TAB_CHANGED, { tabId: newValue.tabid });
    return new RequestHandler().putWithRetry(
      '/preferences',
      {
        yesplanurl: this.state.user.host,
        userid: this.state.user.id
      },
      {
        defaultEventInspectorTabApp: newValue.tabid
      },
      () => {
        const newUser = { ...this.state.user };
        newUser.preferences.defaultEventInspectorTabApp = newValue;
        this.setState({ user: newUser });
      }
    );
  }

  setUserPreferenceEventFilters(filters, doneCallback = false, sendRequest = true) {
    const { host, id } = this.state.user;
    const changeInState = () => {
      /* Deep copy of the user */
      const updatedUser = JSON.parse(JSON.stringify(this.state.user));
      updatedUser.preferences.eventsFiltersApp = filters;
      this.setState({ user: updatedUser }, () => {
        doneCallback && doneCallback();
      });
    };
    if (!sendRequest) return changeInState();
    return new RequestHandler().putWithRetry(
      '/eventFilters',
      {
        yesplanurl: host,
        userid: id
      },
      {
        eventsFiltersApp: filters
      },
      (res, error) => {
        if (!error) {
          changeInState();
        } else {
          return error;
        }
      }
    );
  }

  setCustomRendererForHeaderTitle(renderFn) {
    this.setState({
      customRendererForHeaderTitle: renderFn
    });
  }

  setEventsFilterOptions(options, callback) {
    const filterOptions = reformatRejectedCallsOutOfFilterOptions(options);
    const statuses = {};
    const profiles = {};
    if (filterOptions.statuses.status !== 'rejected') {
      filterOptions.statuses.forEach((status) => {
        statuses[status.id] = status;
      });
    }
    if (filterOptions.profiles.status !== 'rejected') {
      filterOptions.profiles.forEach((profile) => {
        profiles[profile.id] = profile;
      });
    }

    this.setState(
      {
        eventsFilterOptions: filterOptions,
        statuses,
        profiles
      },
      () => {
        callback && callback();
      }
    );
  }

  markAllNotificationsAsRead() {
    const { user } = this.state;
    return new RequestHandler().post(
      '/notifications/markAllAsRead',
      {
        userid: user.id,
        yesplanurl: user.host
      },
      {},
      () => {
        const { rosternotifications, updatenotifications, timesheetnotifications } = this.state;
        this.setState({
          notificationsVersionStamp:
            rosternotifications.markAllAsRead() +
            updatenotifications.markAllAsRead() +
            timesheetnotifications.markAllAsRead()
        });
      }
    );
  }

  fetchEventFilterOptions(callback) {
    const { user, eventsFilterOptions } = this.state;
    /* Avoid unnecessary requests */
    if (eventsFilterOptions === null) {
      return new RequestHandler().get(
        '/eventsFilterOptions',
        {
          yesplanurl: user.host
        },
        (res, error) => {
          if (!error) {
            this.setEventsFilterOptions(res.body, callback);
          } else {
            return error;
          }
        }
      );
    }
  }

  toggleNotificationUnreadStatus(notification, isRead) {
    const { user } = this.state;
    const path = notification.getPath();
    return new RequestHandler().put(
      `/${path}`,
      {
        userid: user.id,
        notificationid: notification.id,
        yesplanurl: user.host
      },
      { id: notification.id, isRead },
      () => {
        const { rosternotifications, updatenotifications, timesheetnotifications } = this.state;
        const notifications = notification.getNotifications(
          rosternotifications,
          updatenotifications,
          timesheetnotifications
        );
        notification.markAsRead();
        notifications.increaseVersionStamp();
        this.setState({
          notificationsVersionStamp:
            rosternotifications.versionStamp +
            updatenotifications.versionStamp +
            timesheetnotifications.versionStamp
        });
      }
    );
  }

  requestAuthWithToken(token) {
    return new RequestHandler().post(
      '/auth',
      {
        jwt: token,
        urldomain: this.props.match.params.domain,
        urlhost: this.props.match.params.host
      },
      {},
      (result, error) => {
        // Remove the query parameters from the URL in the browser, to prevent that bookmarking includes the JWT token
        this.props.redirectToUrl(this.props.location.pathname, {
          hash: this.props.location.hash
        });
        this.processAuth(result, error);
      }
    );
  }

  requestAuthWithSessionStorageJWT() {
    return new RequestHandler().post(
      '/auth',
      {
        urldomain: this.props.match.params.domain,
        urlhost: this.props.match.params.host
      },
      {},
      (result, error) => this.processAuth(result, error)
    );
  }

  processAuth(res, error) {
    if (error || (res && res.body && res.body.token === null)) {
      if (error && !isInternalError(error)) sendToSentry(error);
      if (
        error?.handledByBoundary ||
        (error instanceof ErrorWithHTTPStatusCode &&
          error.code !== 401 &&
          error.code !== 403)
      )
        return;
      sessionStorage.removeItem(sessionStorageItemNameAppJWT);
      this.setState({
        isAuthenticated: false,
        isAuthenticating: false,
        isAccessForbidden: res?.status === 403
      });
    } else {
      const { user, locale, ypversion, featureflags, timeformat, dateformat, dayline } =
        res.body;
      const { setLocale, setCurrentversionNumberAndFeatureFlags, isFeatureActive } =
        this.props;
      /* Setup Sentry */
      setUserInSentryScope(user);
      setHostInSentryScope(user.host);
      setLocaleInSentryScope(locale);
      /* Identify user in PostHog */
      /* Split id so it matches the id used in the backoffice, so the users can be matched in posthog */
      identify(user.id.split('-')[0], identifyProps(user));
      const tenant = getTenantFromHost(user.host);
      capture(PH_USER_LOGGED_IN, { tenant });
      /* Reset navigation history urls */
      this.NavigationHistory.resetAfterLogin();
      /* Setup locale */
      moment.locale(locale);
      setLocale(locale);
      const setTimeFormat = timeformat || defaultTimeFormat;

      document.documentElement.setAttribute('lang', locale);
      /* Set up the yp version number */

      setCurrentversionNumberAndFeatureFlags(ypversion, featureflags, () => {
        const widgetsFiltered = res.body.viewablewidgets.filter((widget) => {
          const hasFeatureFlag = featureflags[widget.feature] !== undefined;
          return hasFeatureFlag ? isFeatureActive(widget.feature) : true;
        });
        this.setupRoutingPaths(widgetsFiltered);
        this.NavigationHistory.setWidgets(widgetsFiltered);
        this.NavigationHistory.setPopoverWidgets(popoverWidgets);

        sessionStorage.setItem(sessionStorageItemNameAppJWT, res.body.token);
        const resBodyWithoutToken = { ...res.body };
        delete resBodyWithoutToken.token;
        const { humanResource } = resBodyWithoutToken;
        if (humanResource && humanResource.rosterpublisheduntil)
          humanResource.rosterpublisheduntil = deserializeYesplanAPIDateAndTime(
            humanResource.rosterpublisheduntil
          );
        this.setState(
          {
            isAuthenticating: false,
            authenticationTime: todayDateObject(),
            isAuthenticated: true,
            ...{ navs: widgetsFiltered, ...resBodyWithoutToken },
            visibletabs: resBodyWithoutToken.visibletabs,
            humanResource,
            dateFormatSetting: dateformat,
            timeFormatSetting: timeformat,
            dayline
          },
          () => {
            /* Add visible widgets for this user as breadcrumb in sentry */
            addInfoBreadcrumbToSentry('widgets', 'visible widgets for user set', {
              widgets: widgetsFiltered.map((w) => w.id).join(',')
            });
            /* Install service worker and ask for permission for push notifications */
            installPushNotifications();
            /* Setup add to homescreen banner */
            const addToHomeScreenString = i18n('Install the Yesplan App');
            addToHomeScreenSetup(addToHomeScreenString);
            /* Setup polling for notifications */
            this.getNotifications(ypversion);
          }
        );
      });


    }
  }

  isAuthenticating() {
    return this.state.isAuthenticating;
  }

  isLoggedIn() {
    return this.state.isAuthenticated;
  }

  isAccessForbidden() {
    return this.state.isAccessForbidden;
  }

  logoutOfApp(whenDoneCallback) {
    addInfoBreadcrumbToSentry('auth', 'Logging user out of app..');
    sessionStorage.removeItem(sessionStorageItemNameAppJWT);
    this.setState({ isAuthenticated: false, isAuthenticating: false }, whenDoneCallback);
    stopPollingForNotifications();
    new RequestHandler().post('/logout', {}, {}, () => {
      addInfoBreadcrumbToSentry('auth', 'User logged out of app');
    });
  }

  redirectToLogin() {
    this.props.redirectToYesplanLogin(false);
    return <div>Redirecting </div>;
  }

  showNotificationsInHeaderForWidget(widgetName) {
    const widget = this.state.navs.find((nav) => nav.nameMessage === widgetName);
    return widget && !widget.internal;
  }

  renderHeaderForWidgetPage(combinedProps, headerTitle, customGoBackToNameFunction) {
    const propsForHeader = { ...combinedProps };
    if (customGoBackToNameFunction)
      propsForHeader.goBackToNameFunction = customGoBackToNameFunction;
    return (
      <WidgetPageHeader
        ref={this.widgetPageHeaderRef}
        {...propsForHeader}
        headerTitle={headerTitle}
        showNotifications={this.showNotificationsInHeaderForWidget(
          this.NavigationHistory.getAppNameForPath(combinedProps.location.pathname)
        )}
        customRendererHeaderTitle={this.state.customRendererForHeaderTitle}
      />
    );
  }

  /* Because the Header components have a separate Router.Switch and can't be animated on mounting/unmounting
     without introducing unnecessary updates that result in a flicker in the header when navigating the app,
     we must render the header of the previous page when opening a Popover page.
     Otherwise, no header is rendered, leading to an empty spot while the Popover page is animating to its final position */
  renderHeaderForPopoverPage(combinedProps) {
    const previousURL = this.NavigationHistory.peekAt(1);
    if (previousURL) {
      if (this.NavigationHistory.willNavigateBackHome()) {
        return <HomePageHeader {...combinedProps} />;
      }
      const headerTitle = previousURL
        ? i18n(this.NavigationHistory.getAppNameForPath(previousURL.pathname))
        : false;
      const goBackToNameFunction = (navigationHistory) => {
        const goBackToURL = navigationHistory.peekAt(2);
        if (
          goBackToURL &&
          !navigationHistory.isHomepageURL(goBackToURL.pathname) &&
          !navigationHistory.isPopoverURL(goBackToURL.pathname)
        ) {
          return i18n(navigationHistory.getAppNameForPath(goBackToURL.pathname));
        }
        return i18n('Home');
      };
      return this.renderHeaderForWidgetPage(
        combinedProps,
        headerTitle,
        goBackToNameFunction
      );
    }
    return null;
  }

  renderHeader(extraProps) {
    const { navs } = this.state;
    return (
      <Routes>
        <Route
          exact
          path=""
          element={<HomePageHeader {...this.props} {...extraProps} />}
        />
        {popoverWidgets.map((popoverWidget) => (
          <Route
            key={popoverWidget.id}
            exact
            path={`/${popoverWidget.id}`}
            element={this.renderHeaderForPopoverPage({
              ...this.props,
              ...extraProps
            })}
          />
        ))}
        {navs.map((widget) => {
          const headerTitle = this.props.location.state
            ? this.props.location.state.headerTitle
            : false;
          return (
            <Route
              key={widget.id}
              path={`/${widget.id}/*`}
              element={this.renderHeaderForWidgetPage(
                {
                  ...this.props,
                  ...extraProps
                },
                headerTitle
              )}
            />
          );
        })}
      </Routes>
    );
  }

  render() {
    const { isValidUrl, history, location, routeUrlFor, match } = this.props;
    const {
      user,
      visibletabs,
      navs,
      statuses,
      profiles,
      dateFormatSetting,
      timeFormatSetting
    } = this.state;
    if (!this.isLoggedIn()) {
      if (this.isAuthenticating()) {
        return (
          <DelayRenderComponent delay={1000}>
            <Authenticating />
          </DelayRenderComponent>
        );
      }
      if (this.isAccessForbidden()) {
        addCriticalBreadcrumbToSentry('auth', 'Access forbidden');
        return (
          // Error message always in English since we have no clue what language the user is using ... it is forbidden
          <ErrorPage errorCode={403} />
        );
      }
      addInfoBreadcrumbToSentry('auth', 'Redirecting user to login page');
      return this.redirectToLogin();
    }
    if (!isValidUrl(location.pathname, user.host)) {
      addCriticalBreadcrumbToSentry('routing', 'Not a valid url', {
        url: location.pathname,
        host: match.params.user.host
      });
      return <ErrorPage errorCode={404} />;
    }
    const childProps = {
      ...this.state,
      previousURL: this.previousURL,
      navigationHistory: this.NavigationHistory,
      widgetPageHeaderRef: this.widgetPageHeaderRef,
      widgetScrollContainerRef: this.widgetScrollContainerRef,
      visibletabs
    };
    this.functionsForChildComponents.forEach(
      (fnName) => (childProps[fnName] = this[fnName])
    );
    history.previousURL = this.previousURL || false;
    this.previousURL = location.pathname;

    return (
      <FormatSettingsContext.Provider
        value={{
          dateFormatSetting: this.state.dateFormatSetting,
          timeFormatSetting: this.state.timeFormatSetting
        }}
      >
        <SessionStorageContext.Provider value={this.sessionStorageContext}>
          <CacheResultsContext.Provider value={this.cacheResultsContext}>
            <div
              data-container="appFrame"
              ref={this.widgetScrollContainerRef}
              tw="absolute top-0 min-w-full max-w-full min-h-full z-1000 bg-silver-lighten-30 overflow-y-auto overflow-x-hidden"
            >
              {this.renderHeader(childProps)}
              <AnimatePresence
                custom={{
                  currentURL: location.pathname,
                  previousURL: history.previousURL
                }}
              >
                <Routes location={location} key={location.pathname}>
                  {/* Homepage */}
                  <Route
                    exact
                    path=""
                    element={<DashboardPage {...this.props} {...childProps} />}
                  />
                  {/* Popovers */}
                  {popoverWidgets.map((popoverWidget) => {
                    const PopoverContent = popoverWidget.component;
                    return (
                      <Route
                        exact
                        key={popoverWidget.id}
                        path={`/${popoverWidget.id}`}
                        element={
                          <ErrorBoundary>
                            <PopoverContent
                              {...this.props}
                              dateFormatSetting={dateFormatSetting}
                              timeFormatSetting={timeFormatSetting}
                              {...childProps}
                            />
                          </ErrorBoundary>
                        }
                      />
                    );
                  })}
                  {/* Widgets */}
                  {navs.map((widget) => (
                    <Route
                      path={`/${widget.id}/*`}
                      key={widget.id}
                      element={
                        <WidgetPage
                          ref={this.widgetPageHeaderRef}
                          scrollContainerRef={this.widgetScrollContainerRef}
                          statuses={statuses}
                          profiles={profiles}
                          dateFormatSetting={dateFormatSetting}
                          timeFormatSetting={timeFormatSetting}
                          {...this.props}
                          {...childProps}
                        />
                      }
                    />
                  ))}
                  {/* 404 */}
                  {
                    <Route
                      path={'/*'}
                      element={
                        <ErrorPage
                          errorCode={404}
                          navigationHistory={this.NavigationHistory}
                          match={{ params: this.props.router.params }}
                        />
                      }
                    />
                  }
                </Routes>
              </AnimatePresence>
            </div>
          </CacheResultsContext.Provider>
        </SessionStorageContext.Provider>
      </FormatSettingsContext.Provider>
    );
  }
}

YesplanAppFrame_.propTypes = {
  match: PropTypes.shape({
    url: PropTypes.string,
    params: PropTypes.shape({
      domain: PropTypes.string,
      host: PropTypes.string
    }).isRequired
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
    state: PropTypes.shape({
      headerTitle: PropTypes.string
    })
  }).isRequired,
  routeUrlFor: PropTypes.func.isRequired,
  linkUrlFor: PropTypes.func.isRequired,
  redirectToYesplanLogin: PropTypes.func.isRequired,
  isValidUrl: PropTypes.func.isRequired,
  redirectToUrl: PropTypes.func.isRequired,
  setLocale: PropTypes.func.isRequired,
  setCurrentversionNumberAndFeatureFlags: PropTypes.func.isRequired,
  history: PropTypes.shape({
    previousURL: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
  }).isRequired
};

const wrapper = function (props) {
  return (
    <FeatureFlagProvider>
      <YesplanLocationUrlWrapper {...props}>
        <DetectServerUpdateBoundary>
          {(propsFromDetectServerUpdateBoundary) => (
            <NetworkDisconnectionBoundary {...propsFromDetectServerUpdateBoundary}>
              {(propsFromNetworkDisconnectionBoundary) => (
                <YesplanAppFrame_
                  {...propsFromNetworkDisconnectionBoundary}
                  ref={propsFromNetworkDisconnectionBoundary.innerRef}
                />
              )}
            </NetworkDisconnectionBoundary>
          )}
        </DetectServerUpdateBoundary>
      </YesplanLocationUrlWrapper>
    </FeatureFlagProvider>
  );
};

const YesplanAppFrame = withRouter(wrapper);
export default YesplanAppFrame;
